import { Component, h, Host, State, Element, Prop } from "@stencil/core";
import { replaceUrlParameter } from "./../../utils/replaceUrlParameter";

@Component({
    tag: "ux-iws-shop-product-line-item",
    styleUrl: "shop-product-line-item.scss",
    shadow: false,
})
export class ShopProductLineItem {
    private baseClass: string = "ux-iws-shop-product-line-item";

    @Element() domNode: HTMLUxIwsShopProductLineItemElement;

    /** when this is set, the component will request data on its own */
    @Prop() priceEndpoint: string = null;

    @State() updateCount: number = 0;

    componentDidLoad() {
        this.addLoadingIndicator();
        this.getAsyncContent();
        this.initQuantityInput();
    }

    componentDidUpdate() {
        this.getAsyncContent();
    }

    private async getAsyncContent() {
        const priceEndpointByQuantity = this.updateQuantityInEndpointUrl();

        const priceData = await fetch(priceEndpointByQuantity, {
            method: "GET",
        });

        const priceDataJson = await priceData.json();

        this.updateSku(priceDataJson);
        this.updateLineItemPrices(priceDataJson);
        this.updateAvailabilityData(priceDataJson);
        this.updateSubItemPrices(priceDataJson);
    }

    private updateQuantityInEndpointUrl = (): string => {
        const quantityField = this.domNode.querySelector(
            ".ux-iws-shop-lineitem__price .ux-number-input__input"
        );

        if (quantityField === null) {
            return this.priceEndpoint;
        }
        const quantity = quantityField["value"];

        return replaceUrlParameter(this.priceEndpoint, "Quantity", quantity);
    };

    private updateQuantityInAllHrefUrls = (quantity: number): void => {
        const elementsToUpdate = this.domNode.querySelectorAll(
            ".ux-iws-shop-lineitem__has-quantity-url"
        );

        elementsToUpdate.forEach(function (elementToUpdate) {
            const currentHrefUrl = elementToUpdate.getAttribute("href");

            if (currentHrefUrl === null) {
                return;
            }

            const updatedHrefUrl = replaceUrlParameter(
                currentHrefUrl,
                "Quantity",
                quantity
            );
            elementToUpdate.setAttribute("href", updatedHrefUrl);
        });
    };

    private updateAvailabilityData = (priceData) => {
        const availabilityNode = this.domNode.querySelector(
            ".text-availability"
        ) as HTMLElement | null;

        if (availabilityNode === null) {
            return;
        }

        if (priceData.availability == "GREEN") {
            availabilityNode.classList.add("text-availability--available");

            availabilityNode.innerHTML = availabilityNode.dataset.textGreen;
        } else if (
            priceData.availability == "YELLOW" &&
            "availabilityDate" in priceData
        ) {
            availabilityNode.classList.add("text-availability--limited");

            availabilityNode.innerHTML =
                availabilityNode.dataset.textYellow +
                " " +
                priceData.availabilityDate;
        } else {
            availabilityNode.classList.add("text-availability--sold");

            availabilityNode.innerHTML = "";
        }
    };

    private updateLineItemPrices = (priceData) => {
        const listPriceNode = this.domNode.querySelector(
            ".ux-iws-shop-lineitem__list-price"
        );
        const myPriceNode = this.domNode.querySelector(
            ".ux-iws-shop-lineitem__my-price"
        );
        const totalPriceNode = this.domNode.querySelector(
            ".ux-iws-shop-lineitem__price__sum"
        );
        const crossPriceNode = this.domNode.querySelector(
            ".ux-iws-shop-lineitem__price__crossprice"
        );
        const crossPriceConditionNode = this.domNode.querySelector(
            ".ux-iws-shop-lineitem__price__crossprice-condition"
        );

        if (listPriceNode != null) {
            listPriceNode.innerHTML = priceData.listPrice;
        }

        if (myPriceNode != null) {
            myPriceNode.innerHTML = priceData.myPrice;
        }

        if (totalPriceNode != null) {
            totalPriceNode.innerHTML = priceData.totalPrice;
        }

        if (priceData.promotion == "true") {
            if (crossPriceNode != null) {
                crossPriceNode.innerHTML = priceData.strikedPrice;
            }

            if (crossPriceConditionNode != null) {
                crossPriceConditionNode.innerHTML = priceData.crossOutCondition;
                crossPriceConditionNode.classList.add("ux-color-error");
            }

            if (totalPriceNode != null) {
                totalPriceNode.classList.add("ux-color-error");
            }
        }
    };

    private updateSubItemPrices = (priceData) => {
        const subItemPriceNodes = this.domNode.querySelectorAll(
            ".ux-iws-shop-lineitem__subitems .price"
        );

        priceData.subItemPrices.forEach(function (subItemPrice, index) {
            if (index < subItemPriceNodes.length) {
                subItemPriceNodes[index].innerHTML = subItemPrice;
            }
        });
    };

    private updateSku = (priceData) => {
        const productSkuNode = this.domNode.querySelector(
            ".ux-iws-shop-lineitem__product-sku"
        );
        if (productSkuNode != null) {
            productSkuNode.textContent = priceData.sku;
        }
    };

    private addLoadingIndicator = () => {
        const loadingIndicatorHtml =
            '<ux-loader variant="horizontal"></ux-loader>';
        const listPriceNode = this.domNode.querySelector(
            ".ux-iws-shop-lineitem__list-price"
        );
        const myPriceNode = this.domNode.querySelector(
            ".ux-iws-shop-lineitem__my-price"
        );
        const totalPriceNode = this.domNode.querySelector(
            ".ux-iws-shop-lineitem__price__sum"
        );
        const crossPriceNode = this.domNode.querySelector(
            ".ux-iws-shop-lineitem__price__crossprice"
        );
        const crossPriceConditionNode = this.domNode.querySelector(
            ".ux-iws-shop-lineitem__price__crossprice-condition"
        );
        const availabilityNode =
            this.domNode.querySelector(".text-availability");

        if (listPriceNode != null) {
            listPriceNode.innerHTML = loadingIndicatorHtml;
        }

        if (myPriceNode != null) {
            myPriceNode.innerHTML = loadingIndicatorHtml;
        }

        if (totalPriceNode != null) {
            totalPriceNode.innerHTML = loadingIndicatorHtml;
            totalPriceNode.classList.remove("ux-color-error");
        }
        this.domNode
            .querySelectorAll(".ux-iws-shop-lineitem__subitems .price")
            .forEach(function (subItemPriceNode) {
                subItemPriceNode.innerHTML = loadingIndicatorHtml;
            });

        if (availabilityNode != null) {
            availabilityNode.innerHTML = loadingIndicatorHtml;
            availabilityNode.classList.remove("text-availability--available");
            availabilityNode.classList.remove("text-availability--limited");
            availabilityNode.classList.remove("text-availability--sold");
        }

        if (crossPriceNode != null) {
            crossPriceNode.innerHTML = "";
        }

        if (crossPriceConditionNode != null) {
            crossPriceConditionNode.innerHTML = "";
            crossPriceConditionNode.classList.remove("ux-color-error");
        }
    };

    private initQuantityInput = () => {
        const quantityInput = this.domNode.querySelector(
            ".ux-iws-shop-lineitem__price .ux-number-input"
        );

        if (quantityInput != null) {
            quantityInput.addEventListener(
                "uxNumberInputChange",
                (event: CustomEvent<{ currentValue: number }>) => {
                    const quantityUpdateUrl = (
                        event.target as HTMLTextAreaElement
                    ).getAttribute("data-quantity-update-url");
                    const url = `${quantityUpdateUrl}&Quantity=${event.detail.currentValue}`;
                    this.updateQuantityInAllHrefUrls(event.detail.currentValue);
                    this.addLoadingIndicator();

                    fetch(url, {
                        method: "GET",
                    }).then((response) => {
                        if (response != null) {
                            this.updateCount++;
                        }
                    });
                }
            );
        }
    };

    /**
     * Render ListItem
     */
    render() {
        return (
            <Host class={`${this.baseClass}`}>
                <slot name="WishlistItem" />
            </Host>
        );
    }
}
